"use client"

import * as React from "react"
import { Label } from "@/components/ui/label"
import { log } from '@/lib/logger'
import { Settings as SettingsIcon, Globe, Clock, Key, Loader2, MessageSquare, CheckCircle, AlertCircle } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { useLanguageSwitch } from "@/hooks/use-language-switch"
import { useIsMobile } from "@/hooks/use-mobile"

import { TooltipProvider } from "@/components/ui/tooltip"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { MetaChannelsSettings } from "./MetaChannelsSettings"
import { ApiKeysSettings } from "./ApiKeysSettings"
import { Textarea } from "@/components/ui/textarea"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { conversationsApi } from "@/services/conversationsApi"
import { META_INTEGRATION_ENABLED } from "@/config/env"

interface SettingsProps {
  translations?: Record<string, unknown>
}

type SettingsTabValue = "general" | "api-keys" | "channels"

type GlobalTagPolicyMode = "manual_protegido" | "adaptativo"

// Datos de ejemplo para zonas horarias
// Mantener solo Madrid disponible. Otras zonas quedan comentadas para referencia futura.
const timezones = [
  { value: "Europe/Madrid", label: "Madrid (GMT+1)", flag: "🇪🇸" },
  // { value: "Europe/London", label: "Londres (GMT+0)", flag: "🇬🇧" },
  // { value: "America/New_York", label: "Nueva York (GMT-5)", flag: "🇺🇸" },
  // { value: "America/Los_Angeles", label: "Los Ángeles (GMT-8)", flag: "🇺🇸" },
  // { value: "Asia/Tokyo", label: "Tokio (GMT+9)", flag: "🇯🇵" },
  // { value: "Australia/Sydney", label: "Sídney (GMT+11)", flag: "🇦🇺" },
]

// Datos de ejemplo para idiomas
// Limitar a Español e Inglés. Otros idiomas comentados para posible reactivación.
const languages = [
  { value: "es", label: "Español", flag: "🇪🇸", native: "Español" },
  { value: "en", label: "Inglés", flag: "🇺🇸", native: "English" },
  // { value: "fr", label: "Francés", flag: "🇫🇷", native: "Français" },
  // { value: "de", label: "Alemán", flag: "🇩🇪", native: "Deutsch" },
  // { value: "it", label: "Italiano", flag: "🇮🇹", native: "Italiano" },
  // { value: "pt", label: "Portugués", flag: "🇵🇹", native: "Português" },
]


export function Settings({
  translations,
}: SettingsProps) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { switchLanguage, currentLanguage } = useLanguageSwitch()
  const isMobile = useIsMobile()
  
  const [selectedTimezone, setSelectedTimezone] = React.useState("Europe/Madrid")
  
  const [generalFeedback, setGeneralFeedback] = React.useState("")
  const [generalFeedbackList, setGeneralFeedbackList] = React.useState<Array<{ id: string | number; feedback: string; created_at?: string }>>([])
  const [generalFeedbackLoading, setGeneralFeedbackLoading] = React.useState(false)
  const [generalFeedbackError, setGeneralFeedbackError] = React.useState<string | null>(null)
  const [generalFeedbackSuccess, setGeneralFeedbackSuccess] = React.useState<string | null>(null)

  const fetchGeneralFeedback = React.useCallback(async () => {
    try {
      setGeneralFeedbackLoading(true)
      setGeneralFeedbackError(null)
      const data = await conversationsApi.listGeneralFeedback(20)
      const results = Array.isArray(data)
        ? data
        : (data as { results?: Array<{ id: string | number; feedback: string; created_at?: string }> })?.results ?? []
      setGeneralFeedbackList(results)
    } catch (error) {
      console.error("Failed to load general feedback", error)
      setGeneralFeedbackError("No se pudo cargar el feedback general")
    } finally {
      setGeneralFeedbackLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchGeneralFeedback()
  }, [fetchGeneralFeedback])

  React.useEffect(() => {
    if (generalFeedbackSuccess) {
      const timer = setTimeout(() => setGeneralFeedbackSuccess(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [generalFeedbackSuccess])

  // Track original values for change detection
  const [originalSettings] = React.useState({
    timezone: "Europe/Madrid",
  })

  // Use translations prop or fallback to t function
  const getTranslation = (key: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : key
    }
    return t(key)
  }

  const [selectedLanguage, setSelectedLanguage] = React.useState(currentLanguage)
  const [hasLanguageChanges, setHasLanguageChanges] = React.useState(false)
  const [globalTagPolicy, setGlobalTagPolicy] = React.useState<GlobalTagPolicyMode>("manual_protegido")
  const [loadedGlobalTagPolicy, setLoadedGlobalTagPolicy] = React.useState<GlobalTagPolicyMode>("manual_protegido")

  const activeTab = React.useMemo<SettingsTabValue>(() => {
    const requestedTab = searchParams.get("tab")

    if (requestedTab === "api-keys" || requestedTab === "channels") {
      return requestedTab
    }

    return "general"
  }, [searchParams])

  const handleTabChange = React.useCallback((nextTab: string) => {
    if (nextTab !== "general" && nextTab !== "api-keys" && nextTab !== "channels") {
      return
    }

    const nextSearchParams = new URLSearchParams(searchParams)

    if (nextTab === "general") {
      nextSearchParams.delete("tab")
    } else {
      nextSearchParams.set("tab", nextTab)
    }

    setSearchParams(nextSearchParams, { replace: true })
  }, [searchParams, setSearchParams])

  React.useEffect(() => {
    let cancelled = false
    const loadGlobalTagPolicy = async () => {
      try {
        const response = await conversationsApi.getGlobalTagPolicy() as { policy_mode?: GlobalTagPolicyMode }
        const mode = response?.policy_mode === "adaptativo" ? "adaptativo" : "manual_protegido"
        if (!cancelled) {
          setGlobalTagPolicy(mode)
          setLoadedGlobalTagPolicy(mode)
        }
      } catch (error) {
        console.error("Failed to load global tag policy", error)
      }
    }
    loadGlobalTagPolicy()
    return () => {
      cancelled = true
    }
  }, [])

  // Update selectedLanguage when currentLanguage changes
  React.useEffect(() => {
    setSelectedLanguage(currentLanguage)
    setHasLanguageChanges(false)
  }, [currentLanguage])

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage)
    setHasLanguageChanges(newLanguage !== currentLanguage)
  }

  const handleSubmitGeneralFeedback = async () => {
    if (!generalFeedback.trim() || generalFeedbackLoading) return
    setGeneralFeedbackLoading(true)
    setGeneralFeedbackError(null)
    try {
      await conversationsApi.submitGeneralFeedback(generalFeedback.trim())
      setGeneralFeedbackSuccess("Feedback enviado correctamente")
      setGeneralFeedback("")
      fetchGeneralFeedback()
    } catch (error) {
      console.error("Failed to submit general feedback", error)
      setGeneralFeedbackError("No se pudo enviar el feedback general. Intenta nuevamente.")
    } finally {
      setGeneralFeedbackLoading(false)
    }
  }

  // Check if there are any changes to apply
  const hasAnyChanges = React.useMemo(() => {
    return hasLanguageChanges ||
           selectedTimezone !== originalSettings.timezone ||
           globalTagPolicy !== loadedGlobalTagPolicy
  }, [hasLanguageChanges, selectedTimezone, originalSettings, globalTagPolicy, loadedGlobalTagPolicy])

  const handleApplyAllChanges = () => {
    const applyAsync = async () => {
    // Apply language changes if any
    if (hasLanguageChanges) {
      switchLanguage(selectedLanguage)
      setHasLanguageChanges(false)
    }
    
    if (globalTagPolicy !== loadedGlobalTagPolicy) {
      await conversationsApi.updateGlobalTagPolicy(globalTagPolicy)
      setLoadedGlobalTagPolicy(globalTagPolicy)
    }
    log.debug('All changes applied successfully')
    }
    applyAsync().catch((error) => {
      console.error("Failed applying settings", error)
    })
  }

  const getCurrentTime = (timezone: string) => {
    return new Date().toLocaleTimeString('es-ES', { 
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    })
  }



  return (
    <TooltipProvider>
      <div className="flex h-full flex-col">
        {/* Header - hidden on mobile */}
        {!isMobile && (
          <div className="flex items-center justify-between p-3 md:p-6 border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                <SettingsIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">{getTranslation('settings.title')}</h1>
                <p className="text-sm text-muted-foreground">{getTranslation('settings.subtitle')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Settings Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={cn("p-3", !isMobile && "p-6")}>
            <div className="w-full">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4 md:space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="general" className="flex items-center gap-1 md:gap-2">
                    <SettingsIcon className="h-4 w-4" />
                    General
                  </TabsTrigger>
                  <TabsTrigger value="api-keys" className="flex items-center gap-1 md:gap-2">
                    <Key className="h-4 w-4" />
                    {isMobile ? 'API Keys' : (getTranslation('settings.api_keys.tab_title') || 'API Keys')}
                  </TabsTrigger>
                  <TabsTrigger value="channels" className="flex items-center gap-1 md:gap-2">
                    <MessageSquare className="h-4 w-4 shrink-0" />
                    {isMobile ? 'Meta' : (getTranslation('settings.meta_channels.tab_title') || 'Meta Channels')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4 md:space-y-4">
                  {/* Zona Horaria Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h2 className="text-lg font-semibold">{getTranslation('settings.timezone_section')}</h2>
                        <p className="text-sm text-muted-foreground">{getTranslation('settings.timezone_desc')}</p>
                      </div>
                    </div>
                    
                    <Card>
                      <CardContent className="p-3 md:p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{getTranslation('settings.timezone_label')}</Label>
                            <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona una zona horaria" />
                              </SelectTrigger>
                              <SelectContent>
                                {timezones.map((timezone) => (
                                  <SelectItem key={timezone.value} value={timezone.value}>
                                    <div className="flex items-center gap-2">
                                      <span>{timezone.flag}</span>
                                      <span>{timezone.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>{getTranslation('settings.current_time')}</Label>
                            <div className="h-10 bg-muted rounded-md flex items-center justify-center">
                              <div className="text-center">
                                <div className="text-lg font-mono font-semibold">
                                  {getCurrentTime(selectedTimezone)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Global Tag Policy Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h2 className="text-lg font-semibold">Política global de tags</h2>
                        <p className="text-sm text-muted-foreground">
                          Controla la seguridad de auto-unassign para todas las conversaciones.
                        </p>
                      </div>
                    </div>

                    <Card>
                      <CardContent className="p-3 md:p-6 space-y-2">
                        <div className="space-y-2">
                          <Label>Tag assignment mode (global)</Label>
                          <Select
                            value={globalTagPolicy}
                            onValueChange={(value) => setGlobalTagPolicy(value as GlobalTagPolicyMode)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona una política" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual_protegido">Modo estricto (manual protegido)</SelectItem>
                              <SelectItem value="adaptativo">Modo adaptativo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {globalTagPolicy === "adaptativo"
                            ? "Modo adaptativo: el motor IA asigna y desasigna tags automáticamente cuando supera los umbrales de confianza configurados."
                            : "Modo estricto: el motor IA asigna tags automáticamente, pero nunca desasigna tags que fueron asignados manualmente."}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Idioma Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h2 className="text-lg font-semibold">{getTranslation('settings.language_section')}</h2>
                        <p className="text-sm text-muted-foreground">{getTranslation('settings.language_desc')}</p>
                      </div>
                    </div>
                    
                    <Card>
                      <CardContent className="p-3 md:p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{getTranslation('settings.language')}</Label>
                            <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona un idioma" />
                              </SelectTrigger>
                              <SelectContent>
                                {languages.map((language) => (
                                  <SelectItem key={language.value} value={language.value}>
                                    <div className="flex items-center gap-2">
                                      <span>{language.flag}</span>
                                      <div className="flex flex-col">
                                        <span>{language.label}</span>
                                        <span className="text-xs text-muted-foreground">{language.native}</span>
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>{getTranslation('settings.preview')}</Label>
                            <div className="h-10 bg-muted rounded-md flex items-center justify-center">
                              <div className="text-center">
                                <div className="font-medium">
                                  {languages.find(l => l.value === selectedLanguage)?.flag} {languages.find(l => l.value === selectedLanguage)?.native}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* General Feedback Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h2 className="text-lg font-semibold">Feedback general</h2>
                        <p className="text-sm text-muted-foreground">
                          Comparte mejoras globales y revisa lo que otros han reportado.
                        </p>
                      </div>
                    </div>

                    <Card>
                      <CardContent className="p-3 md:p-6 space-y-4">
                        {generalFeedbackSuccess && (
                          <Alert className="border-success bg-success">
                            <CheckCircle className="h-4 w-4 text-success" />
                            <AlertDescription className="text-success">
                              {generalFeedbackSuccess}
                            </AlertDescription>
                          </Alert>
                        )}

                        {generalFeedbackError && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{generalFeedbackError}</AlertDescription>
                          </Alert>
                        )}

                        <div className="space-y-2">
                          <Textarea
                            value={generalFeedback}
                            onChange={(e) => setGeneralFeedback(e.target.value)}
                            placeholder="¿Qué podría mejorar la IA en general? ¿Nuevas funcionalidades? ¿Cambios en el comportamiento?"
                            className="min-h-[120px]"
                            disabled={generalFeedbackLoading}
                          />
                          <div className="flex justify-end">
                            <Button
                              onClick={handleSubmitGeneralFeedback}
                              disabled={!generalFeedback.trim() || generalFeedbackLoading}
                            >
                              {generalFeedbackLoading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Enviando...
                                </>
                              ) : (
                                "Enviar feedback"
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <div className="text-sm font-semibold text-muted-foreground">Últimos envíos</div>
                          {generalFeedbackLoading && generalFeedbackList.length === 0 && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" /> Cargando feedback...
                            </div>
                          )}
                          {generalFeedbackList.length === 0 && !generalFeedbackLoading && (
                            <div className="text-sm text-muted-foreground">Aún no hay feedback general.</div>
                          )}
                          {generalFeedbackList.map((entry) => (
                            <div key={entry.id} className="rounded-md border p-3 bg-muted/60">
                              <div className="text-sm text-foreground">{entry.feedback}</div>
                              {entry.created_at && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(entry.created_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="api-keys" className="space-y-4">
                  <ApiKeysSettings />
                </TabsContent>

                <TabsContent value="channels" className="space-y-4">
                  {!META_INTEGRATION_ENABLED && (
                    <Alert>
                      <AlertDescription>
                        Meta integration is disabled in this environment. This section is read-only for QA.
                      </AlertDescription>
                    </Alert>
                  )}
                  <MetaChannelsSettings translations={translations} />
                </TabsContent>


                {/* Action Buttons */}
                <div className={cn("flex pt-4 border-t gap-2", isMobile ? "flex-col" : "items-center justify-between")}>
                  <div className="text-sm text-muted-foreground">
                    {hasAnyChanges ? getTranslation('settings.changes_pending') : getTranslation('settings.changes_will_save')}
                  </div>
                  <div className={cn("flex gap-2", isMobile && "justify-end")}>
                    <Button variant="outline" onClick={() => window.location.href = "/"}>
                      {getTranslation('settings.cancel')}
                    </Button>
                    <Button
                      onClick={handleApplyAllChanges}
                      disabled={!hasAnyChanges}
                    >
                      {getTranslation('settings.apply_changes')}
                    </Button>
                  </div>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
